(() => {
  if (window.hmcSpecTabsInitialised) return;
  window.hmcSpecTabsInitialised = true;

  const productStates = new WeakMap();
  const initialisedWrappers = new WeakSet();
  const injectedTargets = new WeakSet();
  const watchedTemplates = new WeakSet();
  let internalClick = false;
  let scheduled = false;
  let observer;

  const text = value => (value || '').replace(/[\s\u00a0\u200b]+/g, ' ').trim();
  const key = title => text(title.textContent).toLowerCase();
  const placeholder = value => !value || /^(?:n\/?a|not available|not applicable|coming soon|[-–—]+|no (?:technical )?spec(?:ification)?s?(?: (?:available|provided|found))?)[.!]?$/i.test(value);

  function hasSpecs(container) {
    const fragment = container.tagName === 'TEMPLATE' ? container.content : container;
    const values = fragment.querySelectorAll('.hmc-spec-card-value, .hmc-spec-pill');
    if (values.length) return Array.from(values).some(value => !placeholder(text(value.textContent)));
    const copy = fragment.cloneNode(true);
    copy.querySelectorAll('script, style, noscript, template, svg, h1, h2, h3, h4, h5, h6, .hmc-card__header, .hmc-spec-section-title, .hmc-spec-card-label').forEach(node => node.remove());
    const rows = copy.querySelectorAll('tr');
    if (rows.length) {
      return Array.from(rows).some(row => {
        const cells = row.querySelectorAll('td');
        return cells.length > 1 && Array.from(cells).slice(1).some(cell => !placeholder(text(cell.textContent)));
      });
    }
    return !placeholder(text(copy.textContent)) || Boolean(copy.querySelector('img[src], video[src], iframe[src]'));
  }

  function pairs(wrapper) {
    const panels = Array.from(wrapper.querySelectorAll('[role="tabpanel"], .tiny-tabs-content'));
    return Array.from(wrapper.querySelectorAll('[role="tab"][aria-controls]')).map(title => ({
      title,
      panel: panels.find(panel => panel.id === title.getAttribute('aria-controls'))
    })).filter(pair => pair.panel);
  }

  function selected(title) {
    return title.getAttribute('aria-selected') === 'true' || title.classList.contains('tab-active');
  }

  function activate(title) {
    if (selected(title)) return true;
    internalClick = true;
    try { title.click(); } finally { internalClick = false; }
    return selected(title);
  }

  function markEmpty(pair, empty) {
    [pair.title, pair.panel].forEach(node => {
      if (node.hasAttribute('data-hmc-empty-specs') !== empty) node.toggleAttribute('data-hmc-empty-specs', empty);
      if (empty && node.getAttribute('aria-hidden') !== 'true') node.setAttribute('aria-hidden', 'true');
      if (!empty && node.hasAttribute('aria-hidden')) node.removeAttribute('aria-hidden');
    });
    if (empty && pair.title.getAttribute('tabindex') !== '-1') pair.title.setAttribute('tabindex', '-1');
    if (!empty && pair.title.getAttribute('tabindex') === '-1') pair.title.setAttribute('tabindex', '0');
  }

  function update(wrapper) {
    const entries = pairs(wrapper);
    const spec = entries.find(pair => pair.panel.querySelector('[id="hmc-trusted-specs-target"]')) ||
      entries.find(pair => /^(?:spec(?:ification)?s?|technical specifications?)(?:\s|$)/i.test(key(pair.title)));
    if (!spec) return;
    const root = wrapper.closest('[id^="MainProduct-"], .product-section') || document.getElementById('MainContent') || document.body;
    let state = productStates.get(root);
    if (!state) { state = { preferred: null }; productStates.set(root, state); }
    const source = root.querySelector('[id="hmc-specifications-source"]');
    if (source?.tagName === 'TEMPLATE' && observer && !watchedTemplates.has(source)) {
      observer.observe(source.content, { childList: true, subtree: true, characterData: true });
      watchedTemplates.add(source);
    }
    const targets = spec.panel.querySelectorAll('[id="hmc-trusted-specs-target"]');
    targets.forEach(target => {
      if (source) {
        const sourceHtml = hasSpecs(source) ? source.innerHTML.trim() : '';
        if (target.innerHTML.trim() !== sourceHtml) target.innerHTML = sourceHtml;
        injectedTargets.add(target);
      } else if (injectedTargets.has(target) && target.innerHTML.trim()) {
        target.innerHTML = '';
      }
    });
    const available = hasSpecs(spec.panel);
    markEmpty(spec, !available);
    if (!available) {
      if (selected(spec.title)) {
        const fallback = entries.find(pair => pair !== spec && !pair.title.hasAttribute('data-hmc-empty-specs'));
        if (fallback) activate(fallback.title);
        if (selected(spec.title)) {
          internalClick = true;
          try { spec.title.click(); } finally { internalClick = false; }
        }
      }
      return;
    }

    // Move each title with its panel: desktop tabs and mobile accordion keep the same IDs.
    if (spec.title.parentElement === wrapper && spec.panel.parentElement === wrapper && wrapper.firstElementChild !== spec.title) {
      wrapper.insertBefore(spec.panel, wrapper.firstElementChild);
      wrapper.insertBefore(spec.title, spec.panel);
    }
    if (!initialisedWrappers.has(wrapper)) {
      const preferred = state.preferred && entries.find(pair => key(pair.title) === state.preferred && !pair.title.hasAttribute('data-hmc-empty-specs'));
      const desired = preferred || spec;
      if (!state.preferred && !selected(desired.title)) {
        // In a mobile accordion that allows several panels, close the app's old default first.
        entries.filter(pair => pair !== desired && selected(pair.title)).forEach(pair => {
          internalClick = true;
          try { pair.title.click(); } finally { internalClick = false; }
        });
      }
      if (activate(desired.title)) initialisedWrappers.add(wrapper);
    }
  }

  function run() {
    scheduled = false;
    document.querySelectorAll('[id="tiny-tabs-wrapper"]').forEach(update);
  }

  function schedule() {
    if (!scheduled) { scheduled = true; setTimeout(run, 0); }
  }

  document.addEventListener('click', event => {
    if (internalClick || !(event.target instanceof Element)) return;
    const title = event.target.closest('[role="tab"]');
    const wrapper = title?.closest('[id="tiny-tabs-wrapper"]');
    if (!wrapper) return;
    const root = wrapper.closest('[id^="MainProduct-"], .product-section') || document.getElementById('MainContent') || document.body;
    let state = productStates.get(root);
    if (!state) { state = { preferred: null }; productStates.set(root, state); }
    state.preferred = key(title);
  }, true);

  function start() {
    if (window.MutationObserver) {
      observer = new MutationObserver(schedule);
      observer.observe(document.getElementById('MainContent') || document.body, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['aria-selected', 'class', 'style']
      });
    }
    run();
    [100, 500, 1500, 4000].forEach(delay => setTimeout(schedule, delay));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('load', schedule);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('shopify:section:load', schedule);
})();
